import {
  Avatar,
  Collapse,
  Grid,
  Group,
  List,
  Loader,
  RingProgress,
  Stack,
  Text,
  ThemeIcon,
  Title,
  rem,
  Checkbox,
  Button,
  Modal,
  Alert,
} from "@mantine/core";
import React, { memo, useEffect, useMemo, useState } from "react";
import fetchApi from "../../Lib/api";
import { CreateAction, DropDownSelect } from "./Startups";
import { useLocation } from "react-router-dom";
import { IconChevronDown, IconChevronUp, IconCircleCheck, IconUsersGroup } from "@tabler/icons-react";
import moment from "moment";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import useUserStore from "../../../stores/userStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import SquadsDashboard from "../../../stories/SquadsDashboard";
import { useMediaQuery } from "@mantine/hooks";

const statusBadgeClass = (status) => {
  switch ((status || "").toLowerCase()) {
    case "accepted":
      return "bg-green-100 text-green-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    case "submitted":
    case "pending":
    case "reviewed":
      return "bg-blue-100 text-blue-700";
    case "draft":
      return "bg-gray-200 text-gray-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const isInReviewStatus = (status) => ["pending", "submitted", "reviewed"].includes((status || "").toLowerCase());

const formatDisplayDate = (value) => {
  if (!value) return null;
  const date = moment(value);
  if (!date.isValid()) return null;
  return date.format("MMM D, YYYY");
};

const MembersFilters = ({ roles, handleFilterChange }) => {
  const location = useLocation();
  const { t } = useTranslation();

  // Memoize the selectData props for DropDownSelect components
  const memoizedSelectData = useMemo(
    () => ({
      deadlines: [],
      progress: [{ label: "Most Progress" }, { label: "Least Progress" }],
      roles: roles.map((r) => ({ label: r })),
      joinDate: [],
      assignedTasks: [],
    }),
    []
  );

  return (
    <section className="flex justify-center">
      <div className="max-w-7xl w-full p-6">
        <Stack spacing={20}>
          <Group position="apart">
            <Group>
              <ThemeIcon
                variant="gradient"
                size="lg"
                aria-label="Gradient action icon"
                gradient={{ from: "blue", to: "rgba(8, 0, 18, 1)", deg: 90 }}
              >
                <IconUsersGroup size={20} />
              </ThemeIcon>
              <Title order={3} className="text-gray-600">
                {t("workspace.header")}
              </Title>
            </Group>
            <CreateAction />
          </Group>

          <Group className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 w-full">
            <DropDownSelect
              filterKey={"deadlines"}
              selectType={{ label: t("workspace.filters.deadlines") }}
              selectData={memoizedSelectData.deadlines}
              setFilterParams={handleFilterChange}
            />
            <DropDownSelect
              filterKey="progress"
              selectType={{ label: t("workspace.filters.progress") }}
              selectData={memoizedSelectData.progress}
              setFilterParams={handleFilterChange}
            />
            <DropDownSelect
              filterKey="role"
              selectType={{ label: t("workspace.filters.roles") }}
              selectData={memoizedSelectData.roles}
              setFilterParams={handleFilterChange}
            />
            <DropDownSelect
              filterKey="join_date"
              selectType={{ label: t("workspace.filters.join_dates") }}
              selectData={memoizedSelectData.joinDate}
              setFilterParams={handleFilterChange}
              date={true}
            />
            <DropDownSelect
              filterKey="assign_task"
              selectType={{ label: t("workspace.filters.assigned_task") }}
              selectData={memoizedSelectData.assignedTasks}
              setFilterParams={handleFilterChange}
            />
          </Group>
        </Stack>
      </div>
    </section>
  );
};

const MemoMembersFilters = memo(MembersFilters);

// Custom hook for fetching startups
const useStartups = (filterParams) => {
  // Convert filterParams to query string
  const params = new URLSearchParams();
  for (const key in filterParams) {
    params.append(`q[${key}]`, filterParams[key]);
  }
  const queryString = params.toString();

  return useQuery({
    queryKey: ["user-startups", queryString], // Query key includes filter params for caching
    queryFn: async () => {
      const response = await fetchApi(
        `/startup_entities/user_member_startup_entities?${queryString}`,
        "GET"
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch startups");
      }

      return response.json();
    },
    retry: 1, // Retry once on failure
    staleTime: 50 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep inactive data in cache for 30 minutes
  });
};

export default function Workspace() {
  const [filterParams, setFilterParams] = useState({});
  const [selectedStartups, setSelectedStartups] = useState([]);
  const [showMessage, setShowMessage] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {t} = useTranslation();

  const {
    user: { admin },
  } = useUserStore((state) => state.user);

  const handleToggle = () => setShowMessage((prev) => !prev);
  const handleDeleteClick = () => setModalOpened(true);
  const roles = [
    "Founding Team",
    "Member",
    "Intern/Volunteer",
    "Consultant",
    "Investor",
  ];

  const role = (index) => roles[index];

  const { data, isLoading, isError, error } = useStartups(filterParams);
  const queryClient = useQueryClient();

  const [applicationProjectsOpen, setApplicationProjectsOpen] = useState(false);
  const [applicationProjectsData, setApplicationProjectsData] = useState([]);
  const [applicationProjectsLoading, setApplicationProjectsLoading] = useState(false);
  const [applicationProjectsError, setApplicationProjectsError] = useState(null);

  const [userApplicationsOpen, setUserApplicationsOpen] = useState(false);
  const [userApplicationsData, setUserApplicationsData] = useState([]);
  const [userApplicationsLoading, setUserApplicationsLoading] = useState(false);
  const [userApplicationsError, setUserApplicationsError] = useState(null);
  const [userApplicationsStatusCounts, setUserApplicationsStatusCounts] = useState({});
  const [userApplicationsFilter, setUserApplicationsFilter] = useState("all");

  const fetchApplicationProjects = async () => {
    setApplicationProjectsLoading(true);
    setApplicationProjectsError(null);
    try {
      const response = await fetchApi("/startup_entities/application_projects", "GET");
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Unable to load application projects");
      }
      const data = await response.json();
      setApplicationProjectsData(Array.isArray(data.application_projects) ? data.application_projects : []);
    } catch (err) {
      setApplicationProjectsError(err.message || "Unable to load application projects");
    } finally {
      setApplicationProjectsLoading(false);
    }
  };

  const fetchUserApplications = async () => {
    setUserApplicationsLoading(true);
    setUserApplicationsError(null);
    try {
      const response = await fetchApi("/applications", "GET");
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Unable to load applications");
      }
      const data = await response.json();
      setUserApplicationsData(Array.isArray(data.applications) ? data.applications : []);
      setUserApplicationsStatusCounts(data.status_counts || {});
    } catch (err) {
      setUserApplicationsError(err.message || "Unable to load applications");
    } finally {
      setUserApplicationsLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicationProjects();
    fetchUserApplications();
  }, []);

  // Handle select all functionality
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds =
        data?.startup_entities.map((item) => item.startup_entity.id) || [];
      setSelectedStartups(allIds);
    } else {
      setSelectedStartups([]);
    }
  };

  const isAllSelected =
    data?.startup_entities.length > 0 &&
    selectedStartups.length === data?.startup_entities.length;

  const isIndeterminate =
    selectedStartups.length > 0 &&
    selectedStartups.length < (data?.startup_entities.length || 0);

  if (isLoading) {
    return (
      <Loader
        className="mx-auto mt-10"
        color="indigo"
        size="lg"
        variant="dots"
      />
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Error: {error.message}
      </div>
    );
  }
  // Split into projects accepting applications and the rest
  const otherItems = (data?.startup_entities || []).filter(
    (item) => item?.startup_entity?.project_type !== "application"
  );

  const totalApplicationProjects = applicationProjectsData.length;
  const totalUserApplications = userApplicationsData.length;

  const inReviewCount = ["pending", "submitted", "reviewed"].reduce(
    (acc, key) => acc + (userApplicationsStatusCounts?.[key] || 0),
    0
  );
  const acceptedCount = userApplicationsStatusCounts?.accepted || 0;
  const rejectedCount = userApplicationsStatusCounts?.rejected || 0;

  const filteredUserApplications = userApplicationsData.filter((app) => {
    if (userApplicationsFilter === "all") return true;
    const status = (app.status || "").toLowerCase();
    if (userApplicationsFilter === "in_review") {
      return isInReviewStatus(status);
    }
    return status === userApplicationsFilter;
  });

  const statusFilterOptions = [
    { value: "all", label: "All", count: totalUserApplications },
    { value: "in_review", label: "In review", count: inReviewCount },
    { value: "accepted", label: "Accepted", count: acceptedCount },
    { value: "rejected", label: "Rejected", count: rejectedCount },
  ];
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await fetchApi("/startup_entities/bulk_delete", "POST", {
        ids: selectedStartups,
      });
      if (response.ok) {
        await queryClient.invalidateQueries({ queryKey: ["user-startups"] });
        setSelectedStartups([]);
      } else {
        console.log("Error in deleting startups");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <main>
      <MemoMembersFilters roles={roles} handleFilterChange={setFilterParams} />

      <section className="flex justify-center">
        <div className="max-w-7xl w-full px-3 lg:p-6">
          <Modal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            title="Delete projects?"
            centered
            size="md"
          >
            <Stack spacing="md">
              <Alert color="red" variant="light">
                You are about to permanently delete {selectedStartups.length} project{selectedStartups.length === 1 ? '' : 's'}. This action cannot be undone.
              </Alert>
              <Text size="sm" color="dimmed">
                All associated data (tasks, files, and settings) will be removed. Make sure you no longer need these projects.
              </Text>
              <Group position="right" spacing="sm">
                <Button
                  variant="outline"
                  onClick={() => setModalOpened(false)}
                  color="gray"
                >
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={async () => {
                    await handleDelete();
                    setModalOpened(false);
                    setShowMessage(false);
                  }}
                  loading={isDeleting}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : `Delete ${selectedStartups.length} project${selectedStartups.length === 1 ? '' : 's'}`}
                </Button>
              </Group>
            </Stack>
          </Modal>
          <Modal opened={isDeleting} onClose={() => {}} withCloseButton={false} centered size="xs">
            <Stack align="center" py="xl">
              <Loader color="indigo" />
              <Text size="sm" c="dimmed">Deleting selected projects...</Text>
            </Stack>
          </Modal>
          <Stack spacing={16}>
            <SquadsDashboard />

            {(applicationProjectsLoading || applicationProjectsData.length > 0) && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 p-4 text-left"
                onClick={() => setApplicationProjectsOpen((prev) => !prev)}
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Your application projects</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {applicationProjectsLoading
                      ? "Loading summary…"
                      : `You have ${totalApplicationProjects} application project${totalApplicationProjects === 1 ? "" : "s"}`}
                  </p>
                </div>
                <span className="text-gray-500">
                  {applicationProjectsOpen ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                </span>
              </button>
              <Collapse in={applicationProjectsOpen}>
                <div className="border-t border-gray-100 p-4 space-y-4">
                  {applicationProjectsLoading ? (
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <Loader size="sm" />
                      <span>Loading application projects…</span>
                    </div>
                  ) : applicationProjectsError ? (
                    <Alert color="red" variant="light">
                      {applicationProjectsError}
                    </Alert>
                  ) : applicationProjectsData.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      You do not have any application projects yet.
                    </p>
                  ) : (
                    applicationProjectsData.map((project) => {
                      const counts = project.application_counts || {};
                      const total = counts.total || 0;
                      const accepted = counts.accepted || 0;
                      const inReview = counts.in_review || 0;
                      const rejected = counts.rejected || 0;
                      const lastActivity = project.last_activity_at
                        ? moment(project.last_activity_at).fromNow()
                        : null;

                      return (
                        <div
                          key={project.id || project.slug}
                          className="border border-gray-200 rounded-lg bg-gray-50 p-4"
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex-1 min-w-0">
                              <a
                                href={`/projects/${project.slug}`}
                                className="text-base font-semibold text-gray-900 hover:text-blue-600"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {project.name}
                              </a>
                              {project.description && (
                                <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                                  {project.description}
                                </p>
                              )}
                            </div>
                            <span
                              className={`self-start inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                                project.open_for_applications
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {project.open_for_applications ? "Applications open" : "Applications closed"}
                            </span>
                          </div>
                          <div className="mt-4 grid gap-3 grid-cols-2 sm:grid-cols-4">
                            {[{
                              label: "Total",
                              value: total,
                            },
                            {
                              label: "Accepted",
                              value: accepted,
                            },
                            {
                              label: "In review",
                              value: inReview,
                            },
                            {
                              label: "Rejected",
                              value: rejected,
                            }].map((item) => (
                              <div key={item.label} className="rounded-md border border-white/0 bg-white px-3 py-2 shadow-sm">
                                <p className="text-[11px] uppercase tracking-wide text-gray-500">{item.label}</p>
                                <p className="text-sm font-semibold text-gray-900">{item.value}</p>
                              </div>
                            ))}
                          </div>
                          {lastActivity && (
                            <p className="mt-3 text-xs text-gray-500">
                              Last update {lastActivity}
                            </p>
                          )}
                          <div className="mt-4 flex justify-end">
                            <Button
                              size="xs"
                              variant="light"
                              color="blue"
                              component="a"
                              href={`/projects/${project.slug}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Open project
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </Collapse>
            </div>
            )}

            {(userApplicationsLoading || totalUserApplications > 0) && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 p-4 text-left"
                onClick={() => setUserApplicationsOpen((prev) => !prev)}
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Your submitted proposals</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {userApplicationsLoading
                      ? "Loading applications…"
                      : `You have submitted ${totalUserApplications} application${totalUserApplications === 1 ? "" : "s"}`}
                  </p>
                </div>
                <span className="text-gray-500">
                  {userApplicationsOpen ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                </span>
              </button>
              <Collapse in={userApplicationsOpen}>
                <div className="border-t border-gray-100 p-4 space-y-4">
                  {userApplicationsLoading ? (
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <Loader size="sm" />
                      <span>Loading applications…</span>
                    </div>
                  ) : userApplicationsError ? (
                    <Alert color="red" variant="light">
                      {userApplicationsError}
                    </Alert>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        {statusFilterOptions.map((option) => (
                          <Button
                            key={option.value}
                            size="xs"
                            variant={userApplicationsFilter === option.value ? "filled" : "subtle"}
                            color="blue"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUserApplicationsFilter(option.value);
                            }}
                          >
                            {option.label}
                            <span className="ml-1 text-[11px] font-normal text-blue-100/80">
                              ({option.count})
                            </span>
                          </Button>
                        ))}
                      </div>

                      {filteredUserApplications.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          {userApplicationsFilter === "all"
                            ? "You have not submitted any proposals yet."
                            : "No applications match this status."}
                        </p>
                      ) : (
                        filteredUserApplications.map((app) => {
                          const project = app.startup_entity || {};
                          const isSquad = Boolean(app.squad);
                          const members = Array.isArray(app.squad?.members)
                            ? app.squad.members
                            : Array.isArray(app.squad?.all_members)
                            ? app.squad.all_members
                            : [];
                          const memberCount = members.length || (isSquad ? 1 : 0);
                          const submittedDate = formatDisplayDate(app.submitted_at || app.applied_at);
                          const updatedRelative = app.updated_at || app.submitted_at || app.applied_at
                            ? moment(app.updated_at || app.submitted_at || app.applied_at).fromNow()
                            : null;
                          const statusLabel = (app.status || "pending").replace(/_/g, " ");

                          return (
                            <div
                              key={app.id || app.slug}
                              className="border border-gray-200 rounded-lg bg-gray-50 p-4"
                            >
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div className="flex-1 min-w-0">
                                  <a
                                    href={`/projects/${project.slug}`}
                                    className="text-base font-semibold text-gray-900 hover:text-blue-600"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {project.name || "Project"}
                                  </a>
                                  <p className="mt-1 text-xs text-gray-500">
                                    {isSquad
                                      ? `Squad application • ${memberCount} member${memberCount === 1 ? "" : "s"}`
                                      : "Solo application"}
                                  </p>
                                </div>
                                <span className={`self-start inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${statusBadgeClass(app.status)}`}>
                                  {statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
                                </span>
                              </div>
                              {app.pitch && (
                                <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                                  {app.pitch}
                                </p>
                              )}
                              <div className="mt-3 flex flex-col gap-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                                <span>
                                  {submittedDate
                                    ? `Submitted ${submittedDate}`
                                    : app.applied_at
                                    ? `Applied ${formatDisplayDate(app.applied_at)}`
                                    : null}
                                </span>
                                {updatedRelative && <span>Updated {updatedRelative}</span>}
                              </div>
                              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                                {app.accepted_startup_entity?.slug && (
                                  <Button
                                    size="xs"
                                    color="green"
                                    variant="light"
                                    component="a"
                                    href={`/projects/${app.accepted_startup_entity.slug}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Open workspace
                                  </Button>
                                )}
                                <Button
                                  size="xs"
                                  color="blue"
                                  component="a"
                                  href={`/applications/${app.slug}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View application
                                </Button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </>
                  )}
                </div>
              </Collapse>
            </div>
            )}

            {otherItems.map(
              ({
                startup_entity: { id, description, name, entity_type, slug, project_type, open_fo_applications },
                openings_length,
                members,
                member_productivity,
                response_fill_percentage,
                team_role,
                membership_start_date,

                assigned_subcategories,
                unread_contact_messages_count,
                pending_join_requests,
              }) => (
                <StartupCard
                  key={id}
                  details={{
                    startup_entity: {
                      id,
                      description,
                      name,
                      entity_type,
                      slug,
                      project_type,
                      open_fo_applications,
                    },
                    members,
                    member_productivity,
                    response_fill_percentage,
                    team_role,
                    membership_start_date,
                    assigned_subcategories,
                    unread_contact_messages_count,
                    pending_join_requests,
                  }}
                  role={role}
                  selectedStartups={selectedStartups}
                  setSelectedStartups={setSelectedStartups}
                  showMessage={showMessage}
                  setShowMessage={setShowMessage}
                />
              )
            )}
          </Stack>
        </div>
      </section>
    </main>
  );
}

const StartupCard = ({
  details: {
    startup_entity: { id, description, name, entity_type, slug },
    members,
    member_productivity,
    response_fill_percentage,
    team_role,
    membership_start_date,
    assigned_subcategories,
    unread_contact_messages_count,
    pending_join_requests,
  },
  role,
  selectedStartups,
  setSelectedStartups,
  showMessage,
}) => {
  const [checked, setChecked] = useState(false);
  const isMobile = useMediaQuery('(max-width: 834px)');
  useEffect(() => {
    setChecked(selectedStartups.includes(id));
  }, [selectedStartups, id]);

  const handleCardClick = (event) => {
    if (showMessage && !event.target.closest("a")) {
      event.preventDefault();
      handleCheckboxChange({ currentTarget: { checked: !checked } });
    }
  };

  const handleCheckboxChange = (event) => {
    const isChecked = event.currentTarget.checked;
    setChecked(isChecked);

    if (isChecked) {
      setSelectedStartups((prev) => [...prev, id]);
    } else {
      setSelectedStartups((prev) => prev.filter((item) => item !== id));
    }
  };

  return (
    <div
      className={`group flex flex-col h-full bg-white border-2 shadow-sm rounded-xl transition-all duration-200 ${
        showMessage
          ? checked
            ? "border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200"
            : "border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer"
          : "border-gray-200 hover:shadow-md"
      }`}
      onClick={handleCardClick}
    >
      <div className="p-4 md:p-6">
        <Grid columns={24}>
          <Grid.Col span={isMobile ? 16: 10}>
            <Group noWrap position="apart" align="end">
              <div className="flex-1 my-auto">
                <Stack spacing={5}>
                  <Group>
                    {showMessage && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={checked}
                          onChange={handleCheckboxChange}
                          radius="lg"
                          size="md"
                          color="blue"
                          className="transition-all duration-200"
                        />
                      </div>
                    )}
                    <a
                      href={`${
                        entity_type === "Project"
                          ? "/project_entities"
                          : "/projects"
                      }/${slug}`}
                      className="flex-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="block mb-1 text-xs font-semibold uppercase text-amber-500">
                        Project
                      </span>
                      <Stack spacing={0}>
                        <h3 className="text-xl font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
                          {name}
                        </h3>
                        <Text c="dimmed" size="xs" tt="uppercase" fw={700}>
                          {role(team_role)} since{" "}
                          {moment(membership_start_date).format("MMMM D, YYYY")}
                        </Text>
                      </Stack>
                      <p className="mt-3 text-gray-500 line-clamp-2">
                        {description}
                      </p>
                    </a>
                  </Group>
                </Stack>
              </div>
            { !isMobile &&  <RingProgress
                roundCaps
                thickness={5}
                sections={[
                  {
                    value: response_fill_percentage,
                    color: "blue",
                  },
                ]}
                label={
                  <Text c="blue" fw={700} ta="center" size="xl">
                    {response_fill_percentage}%
                  </Text>
                }
              />}
            </Group>
          </Grid.Col>
          <Grid.Col className="hidden md:block h-full" span={7}>
            <MembersProductivity
              members={members}
              member_productivity={member_productivity}
            />
          </Grid.Col>
          <Grid.Col span={ isMobile ? 8: 7}>
            <Stack spacing={10} align="center">
              <Group noWrap position="right">
                <button
                  type="button"
                  className="py-1 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  { isMobile ? "Req/Msg" : t("workspace.requests")}
                  <span className="inline-flex items-center py-0.5 px-1.5 rounded-full text-xs font-medium bg-blue-500 text-white">
                    {unread_contact_messages_count + pending_join_requests}
                  </span>
                </button>
              </Group>
             { !isMobile ?(
                <Stack spacing={10}>         
                <p className="mt-1 text-sm text-gray-500 dark:text-neutral-500">
                  {t("workspace.pending_tasks")}
                </p>
                <List
                  spacing="xs"
                  size="xs"
                  center
                  icon={
                    <ThemeIcon color="teal" size={16} radius="xl">
                      <IconCircleCheck
                        style={{ width: "12px", height: "12px" }}
                      />
                    </ThemeIcon>
                  }
                >
                  {assigned_subcategories.map((task) => (
                    <List.Item key={task.id}>
                      {`${task.name} to be completed before ${task.end_date}`}
                    </List.Item>
                  ))}
                </List>
                </Stack>) :  (
                    <RingProgress
                      size={60}
                      roundCaps
                      thickness={4}
                      sections={[
                        {
                          value: response_fill_percentage,
                          color: "blue",
                        },
                      ]}
                      label={
                        <Text c="blue" fw={700} ta="center" size={"sm"}>
                          {response_fill_percentage}%
                        </Text>
                      }
                    />
                  )
              }
            </Stack>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  );
};

const MembersProductivity = ({ members, member_productivity }) => {
  const {
    user: { id },
  } = useUserStore((state) => state.user);
  const [memId, setMemId] = useState(id);

  return (
    <Stack justify="space-between" className="h-full">
      <Group spacing={5} className="mt-auto mx-auto">
        {members.map((m) => (
          <Avatar
            key={`user_${m.id}_${m.first_name}`}
            src={m.avatar_url}
            radius="xl"
            color="indigo"
            onMouseEnter={() => setMemId(m.id)}
            onMouseLeave={() => setMemId(id)}
            className="cursor-pointer"
          >
            {m.first_name[0]}
            {m.last_name[0]}
          </Avatar>
        ))}
      </Group>
      <ResponsiveContainer width="100%" height={70}>
        <AreaChart data={member_productivity[memId]}>
          <Area
            type="linear"
            dataKey="up"
            stroke="#1c8cdc"
            fill="#1c8cdc"
            fillOpacity={0.3}
          />
          <Area
            type="linear"
            dataKey="ap"
            stroke="#82ca9d"
            fill="#82ca9d"
            fillOpacity={0.1}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Stack>
  );
};
